import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "../services/api";
import { Chart } from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import "chart.js/auto";
import "chartjs-adapter-date-fns";
import { browserTimeZone, formatLocalAxisTime, historyQueryTime } from "../utils/dateTime";
import { acceptExportResponse, clearCursorState, describeExportFailure, historyExportRequest } from "../utils/graphExport";
import GraphPresetBar from "../components/graph/savedViews/GraphPresetBar";
import ChartDisplayPanel from "../components/graph/appearance/ChartDisplayPanel";
import LiveMonitoringPanel from "../components/graph/controls/LiveMonitoringPanel";
import TagSelectionPanel from "../components/graph/controls/TagSelectionPanel";
import StatusEventsControl from "../components/graph/controls/StatusEventsControl";
import TimeRangePanel from "../components/graph/controls/TimeRangePanel";
import RecordsPanel from "../components/graph/records/RecordsPanel";
import { CombinedTrend, TagTrend } from "../components/graph/charts/TrendCharts";
import { liveRequestsAllowed as liveModeAllowed, responseIsCurrent, stopLiveRuntime } from "../utils/graphMode";
import { presetApplication } from "../utils/graphPresets";
import { chartNumericValue, chronologicalSamples, liveQueryBounds, reconcileIncrementalHistory, reconcileLiveHistory, reconcileStatusEvents, remapLiveSelection } from "../utils/liveHistory";
import "./GraphPage.css";

Chart.register(zoomPlugin);

const THEME_STORAGE_KEY = "plcGatewayHomeTheme";
const RECORDS_STORAGE_KEY = "plcGatewayGraphRecordsOpen";
const APPEARANCE_STORAGE_KEY = "plcGatewayGraphAppearance";
const LAYOUT_STORAGE_KEY = "plcGatewayGraphLayout";
const LIVE_STORAGE_KEY = "plcGatewayGraphLive";
const STATUS_EVENTS_KEY = "plcGatewayGraphStatusEvents";
const COMBINED_CHART_ID = "combined";
const LIVE_CHART_DATA = Object.freeze({ live: true });
const LIVE_WINDOWS = [5, 15, 30, 60, 240, 1440];
const LIVE_INTERVALS = [5, 10, 30, 60];

const SERIES_COLORS = {
    light: ["#2563EB", "#047857", "#B45309", "#6D28D9", "#BE123C", "#0F766E", "#9A3412", "#0369A1"],
    dark: ["#38BDF8", "#34D399", "#FBBF24", "#C4B5FD", "#FDA4AF", "#5EEAD4", "#FDBA74", "#7DD3FC"],
};

const selectionGuidePlugin = {
    id: "graphSelectionGuide",
    afterDatasetsDraw(chart) {
        const guide = chart.$selectionGuide;
        if (!guide) {
            return;
        }
        const meta = chart.getDatasetMeta(guide.datasetIndex ?? 0);
        const point = meta?.data?.[guide.index];
        if (!point || point.skip || meta.hidden) {
            return;
        }
        const { ctx, chartArea } = chart;
        const drawCrosshair = (lineWidth, strokeStyle) => {
            ctx.beginPath();
            ctx.lineWidth = lineWidth;
            ctx.strokeStyle = strokeStyle;
            ctx.setLineDash([6, 4]);
            ctx.moveTo(point.x, chartArea.top);
            ctx.lineTo(point.x, chartArea.bottom);
            ctx.moveTo(chartArea.left, point.y);
            ctx.lineTo(chartArea.right, point.y);
            ctx.stroke();
        };
        ctx.save();
        drawCrosshair(4, guide.contrast);
        drawCrosshair(1.5, guide.color);
        ctx.restore();
    },
};

Chart.register(selectionGuidePlugin);

const readRecordsOpen = () => {
    try {
        return localStorage.getItem(RECORDS_STORAGE_KEY) !== "closed";
    } catch (error) {
        console.error("Error reading records panel preference:", error);
        return true;
    }
};

const storeRecordsOpen = (open) => {
    try {
        localStorage.setItem(RECORDS_STORAGE_KEY, open ? "open" : "closed");
    } catch (error) {
        console.error("Error saving records panel preference:", error);
    }
};


const seriesColor = (theme, index) => {
    const palette = SERIES_COLORS[theme] || SERIES_COLORS.light;
    return palette[index % palette.length];
};

const isHexColor = (value) => typeof value === "string" && /^#[0-9A-Fa-f]{6}$/.test(value);

const defaultGridHex = (theme) => (theme === "dark" ? "#94A3B8" : "#64748B");

const defaultGridOpacity = (theme) => (theme === "dark" ? 0.4 : 0.55);

const clampNumber = (value, min, max, fallback) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return fallback;
    }
    return Math.min(max, Math.max(min, number));
};

const APPEARANCE_DEFAULTS = {
    gridColor: null,
    gridOpacity: null,
    lineWidth: 2.5,
    showPoints: true,
    pointSize: 3,
    selectorStyles: [],
};

const readAppearance = () => {
    try {
        const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
        if (!raw) {
            return { ...APPEARANCE_DEFAULTS, selectorStyles: [] };
        }
        const parsed = JSON.parse(raw);
        const selectorStyles = Array.isArray(parsed.selectorStyles)
            ? parsed.selectorStyles.map((style) => ({
                color: isHexColor(style?.color) ? style.color : null,
                colorCustom: Boolean(style?.colorCustom) && isHexColor(style?.color),
            }))
            : [];
        return {
            gridColor: isHexColor(parsed.gridColor) ? parsed.gridColor : null,
            gridOpacity: parsed.gridOpacity == null ? null : clampNumber(parsed.gridOpacity, 0.15, 1, null),
            lineWidth: clampNumber(parsed.lineWidth, 1, 6, 2.5),
            showPoints: parsed.showPoints !== false,
            pointSize: clampNumber(parsed.pointSize, 0, 10, 3),
            selectorStyles,
        };
    } catch (error) {
        console.error("Error reading chart appearance:", error);
        return { ...APPEARANCE_DEFAULTS, selectorStyles: [] };
    }
};

const storeAppearance = (appearance) => {
    try {
        localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(appearance));
    } catch (error) {
        console.error("Error saving chart appearance:", error);
    }
};

const readLayout = () => {
    try {
        return localStorage.getItem(LAYOUT_STORAGE_KEY) === "combined" ? "combined" : "separate";
    } catch (error) {
        console.error("Error reading chart layout:", error);
        return "separate";
    }
};

const storeLayout = (layout) => {
    try {
        localStorage.setItem(LAYOUT_STORAGE_KEY, layout === "combined" ? "combined" : "separate");
    } catch (error) {
        console.error("Error saving chart layout:", error);
    }
};

const readLivePreferences = () => {
    const fallback = { windowMinutes: 60, refreshSeconds: 10 };
    try {
        const parsed = JSON.parse(localStorage.getItem(LIVE_STORAGE_KEY) || "{}");
        const windowMinutes = Number(parsed.windowMinutes);
        const refreshSeconds = Number(parsed.refreshSeconds);
        return {
            windowMinutes: LIVE_WINDOWS.includes(windowMinutes) ? windowMinutes : fallback.windowMinutes,
            refreshSeconds: LIVE_INTERVALS.includes(refreshSeconds) ? refreshSeconds : fallback.refreshSeconds,
        };
    } catch (error) {
        console.error("Error reading live preferences:", error);
        return fallback;
    }
};

const storeLivePreferences = (preferences) => {
    try {
        localStorage.setItem(LIVE_STORAGE_KEY, JSON.stringify({
            windowMinutes: preferences.windowMinutes,
            refreshSeconds: preferences.refreshSeconds,
        }));
    } catch (error) {
        console.error("Error saving live preferences:", error);
    }
};

const formatDateTimeLocal = (date) => {
    const pad = (num) => String(num).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const liveWindowLabel = (minutes) => {
    if (minutes === 1440) {
        return "1440 minutes (24 hours)";
    }
    if (minutes === 240) {
        return "240 minutes (4 hours)";
    }
    return `${minutes} minutes`;
};

const liveLoadWarning = ({ windowMinutes, refreshSeconds, tagCount }) => {
    const fastRefresh = refreshSeconds <= 10;
    const longWindow = windowMinutes >= 240;
    const manyTags = tagCount >= 4;
    const veryLongWindow = windowMinutes >= 1440;
    if (!(fastRefresh && (longWindow || manyTags)) && !(veryLongWindow && refreshSeconds <= 30) && !(tagCount >= 6 && refreshSeconds <= 30)) {
        return "";
    }
    return "This window and refresh can make each history request slow. The next refresh waits until the current request finishes, so a short interval does not guarantee that freshness. With several tags or a long window, 30 or 60 seconds is the safer refresh.";
};

const isAbortError = (error) => (
    error?.code === "ERR_CANCELED"
    || error?.name === "CanceledError"
    || error?.name === "AbortError"
);

const clearRuntimeTimer = (runtime) => {
    if (runtime.timer != null) {
        clearTimeout(runtime.timer);
        runtime.timer = null;
    }
};

const liveRequestsAllowed = (runtime, config) => liveModeAllowed({
    enabled: config.enabled,
    paused: config.paused,
    hidden: document.hidden,
    unmounted: runtime.unmounted,
    mode: config.mode,
});

const readStatusEventsEnabled = () => {
    try {
        return localStorage.getItem(STATUS_EVENTS_KEY) === "true";
    } catch (error) {
        return false;
    }
};

const storeStatusEventsEnabled = (enabled) => {
    try {
        localStorage.setItem(STATUS_EVENTS_KEY, enabled ? "true" : "false");
    } catch (error) {
        console.error("Error saving status-event preference:", error);
    }
};

const emptyHistory = () => ({
    chartData: null,
    tableData: [],
    statusEvents: [],
    chartNote: "",
});

const historyPayload = (chartItem) => ({
    chartData: chartItem.chartData || null,
    tableData: Array.isArray(chartItem.tableData) ? chartItem.tableData : [],
    statusEvents: Array.isArray(chartItem.statusEvents) ? chartItem.statusEvents : [],
    chartNote: chartItem.chartNote || "",
});

const loadStatusEvents = async (tagId, start, end) => {
    try {
        const response = await API.get(
            `/tags/${tagId}/status-events/?start_time=${encodeURIComponent(start)}&end_time=${encodeURIComponent(end)}`
        );
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        return [];
    }
};

const STRING_CHART_NOTE = "String values are stored as text and are not plotted on this numeric chart.";

const chartIsString = (chartItem) => chartItem?.signalType === "STRING" || chartItem?.opcDataType === "String";

const withHistoryRows = (chartItem, rows, statusEvents, { preSorted = false, live = false } = {}) => {
    const samples = preSorted
        ? (Array.isArray(rows) ? rows : [])
        : chronologicalSamples(Array.isArray(rows) ? rows : []);
    const unsupported = live
        ? false
        : samples.some((item) => chartNumericValue(item?.value) == null);
    if (unsupported || chartIsString(chartItem)) {
        return {
            ...chartItem,
            statusEvents: Array.isArray(statusEvents) ? statusEvents : [],
            tableData: [],
            chartData: null,
            chartNote: STRING_CHART_NOTE,
        };
    }
    return {
        ...chartItem,
        statusEvents: Array.isArray(statusEvents) ? statusEvents : [],
        tableData: samples,
        chartData: live ? LIVE_CHART_DATA : {
            labels: samples.map((item) => formatLocalAxisTime(item.timestamp)),
            datasets: [
                {
                    label: chartItem.tagName || `Tag ${chartItem.tagId}`,
                    data: samples.map((item) => item.value),
                    borderWidth: 2,
                    tension: 0,
                },
            ],
        },
        chartNote: "",
    };
};

const createSelector = (id, style) => ({
    id,
    tagId: "",
    tagName: "",
    chartData: null,
    tableData: [],
    color: style?.colorCustom ? style.color : "",
    colorCustom: Boolean(style?.colorCustom),
    visible: true,
    yAxis: "y",
});

const displayColor = (chart, index, theme) => (
    chart?.colorCustom && isHexColor(chart.color) ? chart.color : seriesColor(theme, index)
);
const readPageTheme = () => {
    try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        if (saved === "light" || saved === "dark") {
            return saved;
        }
    } catch (error) {
        console.error("Error reading theme preference:", error);
    }

    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }

    return "light";
};

const QUICK_RANGES = [
    { id: "last_hour", label: "Last Hour" },
    { id: "current_4_hours", label: "Last 4 Hours" },
    { id: "last_10_hours", label: "Last 10 Hours" },
    { id: "current_day", label: "Current Day" },
    { id: "previous_day", label: "Previous Day" },
];

const GraphPage = () => {
    const theme = readPageTheme();
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [tags, setTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(true);
    const [tagsError, setTagsError] = useState("");
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState("");
    const [activeRange, setActiveRange] = useState("");
    const [recordsOpen, setRecordsOpen] = useState(readRecordsOpen);
    const [selection, setSelection] = useState(null);
    const [zoomFlags, setZoomFlags] = useState({});
    const [appearance, setAppearance] = useState(readAppearance);
    const [layout, setLayout] = useState(readLayout);
    const [liveWindow, setLiveWindow] = useState(() => readLivePreferences().windowMinutes);
    const [liveRefresh, setLiveRefresh] = useState(() => readLivePreferences().refreshSeconds);
    const [liveEnabled, setLiveEnabled] = useState(false);
    const [livePaused, setLivePaused] = useState(false);
    const [liveStatus, setLiveStatus] = useState("off");
    const [liveUpdatedAt, setLiveUpdatedAt] = useState("");
    const [liveMessage, setLiveMessage] = useState("");
    const [liveHidden, setLiveHidden] = useState(false);
    const [followLatest, setFollowLatest] = useState(true);
    const [followEpoch, setFollowEpoch] = useState(0);
    const [dataMode, setDataMode] = useState("range");
    const [rangeData, setRangeData] = useState({});
    const [liveData, setLiveData] = useState({});
    const [activeControlTab, setActiveControlTab] = useState("presets");
    const [statusEventsEnabled, setStatusEventsEnabled] = useState(readStatusEventsEnabled);
    const [exporting, setExporting] = useState(false);
    const [exportMessage, setExportMessage] = useState("");
    const appearanceRef = useRef(appearance);
    appearanceRef.current = appearance;
    const liveRuntime = useRef({
        timer: null,
        controller: null,
        generation: 0,
        running: false,
        unmounted: false,
        fullNext: true,
        lastSuccessMs: null,
        historyCursor: null,
        statusEventCursor: null,
    });
    const liveConfig = useRef({
        enabled: false,
        paused: false,
        windowMinutes: liveWindow,
        refreshSeconds: liveRefresh,
        statusEventsEnabled: false,
        mode: "range",
    });
    const chartsRef = useRef(null);
    const historyRef = useRef({ range: {}, live: {} });
    const rangeRuntime = useRef({ controller: null, generation: 0, unmounted: false });
    const runLiveFetchRef = useRef(async () => {});
    liveConfig.current = {
        enabled: liveEnabled,
        paused: livePaused,
        windowMinutes: liveWindow,
        refreshSeconds: liveRefresh,
        statusEventsEnabled,
        mode: dataMode,
    };
    historyRef.current = { range: rangeData, live: liveData };

    // هر آیتم = یک نمودار
    const [selectedCharts, setSelectedCharts] = useState(() => {
        const saved = readAppearance();
        return [createSelector(Date.now(), saved.selectorStyles[0])];
    });
    chartsRef.current = selectedCharts;

    useEffect(() => {
        let active = true;

        const fetchTags = async () => {
            try {
                const response = await API.get("/tags/");
                if (!active) {
                    return;
                }
                setTags(response.data);
                setTagsError("");
            } catch (error) {
                console.error("Error fetching tags:", error);
                if (active) {
                    setTagsError("Unable to load tags.");
                }
            } finally {
                if (active) {
                    setTagsLoading(false);
                }
            }
        };

        fetchTags();

        return () => {
            active = false;
        };
    }, []);

    const selectRecord = useCallback((seriesId, index) => {
        const store = historyRef.current[liveConfig.current.mode === "live" ? "live" : "range"] || {};
        const row = store[seriesId]?.tableData?.[index];
        setSelection({
            seriesId,
            index,
            recordId: row?.id ?? null,
            timestamp: row?.timestamp ?? null,
        });
    }, []);

    const clearCursor = useCallback(() => {
        setSelection((current) => clearCursorState({ selection: current }).selection);
    }, []);

    const chooseDataMode = (mode) => {
        const next = mode === "live" ? "live" : "range";
        if (next === liveConfig.current.mode) {
            return;
        }
        setDataMode(next);
        setSelection(null);
        if (next === "live") {
            liveConfig.current.enabled = true;
            liveConfig.current.paused = false;
            setLiveEnabled(true);
            setLivePaused(false);
            setFollowLatest(true);
            setLiveStatus("loading");
            setLiveMessage("");
        } else {
            liveConfig.current.enabled = false;
            liveConfig.current.paused = false;
            setLiveEnabled(false);
            setLivePaused(false);
            setLiveStatus("off");
            setLiveMessage("");
            setLiveHidden(false);
            stopLiveRuntime(liveRuntime.current);
        }
        if (next !== "range") {
            rangeRuntime.current.generation += 1;
            rangeRuntime.current.controller?.abort();
            rangeRuntime.current.controller = null;
            setHistoryLoading(false);
        }
    };

    const syncZoomFlag = useCallback((chartId, chart, source = "api") => {
        const zoomed = typeof chart?.isZoomedOrPanned === "function" && chart.isZoomedOrPanned();
        setZoomFlags((prev) => {
            if (Boolean(prev[chartId]) === zoomed) {
                return prev;
            }
            return { ...prev, [chartId]: zoomed };
        });
        if (source === "user" && zoomed && liveConfig.current.enabled) {
            setFollowLatest(false);
        }
    }, []);

    const toggleRecords = () => {
        setRecordsOpen((open) => {
            const next = !open;
            storeRecordsOpen(next);
            return next;
        });
    };

    useEffect(() => {
        if (!recordsOpen || !selection) {
            return;
        }
        const row = document.getElementById(`graph-record-${selection.seriesId}-${selection.index}`);
        row?.scrollIntoView({ block: "nearest", inline: "nearest" });
    }, [recordsOpen, selection]);

    const setQuickTimeRange = (range) => {
        const now = new Date();
        let newStartTime, newEndTime;

        switch (range) {
            case "last_hour":
                newStartTime = new Date(now.getTime() - 60 * 60 * 1000);
                newEndTime = new Date();
                break;
            case "current_4_hours":
                newStartTime = new Date(now.getTime() - 4 * 60 * 60 * 1000);
                newEndTime = new Date();
                break;
            case "last_10_hours":
                newStartTime = new Date(now.getTime() - 10 * 60 * 60 * 1000);
                newEndTime = new Date();
                break;
            case "current_day":
                newStartTime = new Date();
                newStartTime.setHours(0, 0, 0, 0);
                newEndTime = new Date();
                break;
            case "previous_day":
                newStartTime = new Date();
                newStartTime.setDate(newStartTime.getDate() - 1);
                newStartTime.setHours(0, 0, 0, 0);

                newEndTime = new Date();
                newEndTime.setDate(newEndTime.getDate() - 1);
                newEndTime.setHours(23, 59, 59, 999);
                break;
            default:
                return;
        }

        setActiveRange(range);
        setStartTime(formatDateTimeLocal(newStartTime));
        setEndTime(formatDateTimeLocal(newEndTime));
    };

    const addNewChartSelector = () => {
        setSelectedCharts((prev) => {
            const saved = appearanceRef.current.selectorStyles?.[prev.length];
            return [
                ...prev,
                createSelector(Date.now() + Math.random(), saved),
            ];
        });
    };

    const removeChartSelector = (chartId) => {
        setSelectedCharts((prev) => prev.filter((item) => item.id !== chartId));
        setRangeData((prev) => {
            if (!prev[chartId]) {
                return prev;
            }
            const next = { ...prev };
            delete next[chartId];
            return next;
        });
        setLiveData((prev) => {
            if (!prev[chartId]) {
                return prev;
            }
            const next = { ...prev };
            delete next[chartId];
            return next;
        });
        setSelection((current) => (current?.seriesId === chartId ? null : current));
        setZoomFlags((prev) => {
            if (!prev[chartId]) {
                return prev;
            }
            const next = { ...prev };
            delete next[chartId];
            return next;
        });
    };

    const updateChartTag = (chartId, tagId) => {
        const tag = tags.find((t) => String(t.id) === String(tagId));

        setSelectedCharts((prev) =>
            prev.map((item) =>
                item.id === chartId
                    ? {
                        ...item,
                        tagId,
                        tagName: tag ? tag.name : "",
                        signalType: tag?.signal_type || "",
                        opcDataType: tag?.opc_data_type || "",
                        chartData: null,
                        tableData: [],
                        chartNote: tag?.signal_type === "STRING" || tag?.opc_data_type === "String" ? STRING_CHART_NOTE : "",
                    }
                    : item
            )
        );
        setRangeData((prev) => ({ ...prev, [chartId]: emptyHistory() }));
        setLiveData((prev) => ({ ...prev, [chartId]: emptyHistory() }));
        setSelection((current) => (current?.seriesId === chartId ? null : current));
    };

    const updateAppearance = (patch) => {
        setAppearance((prev) => {
            const next = { ...prev, ...patch };
            storeAppearance(next);
            return next;
        });
    };

    const resetAppearance = () => {
        setAppearance((prev) => {
            const next = {
                ...APPEARANCE_DEFAULTS,
                selectorStyles: prev.selectorStyles,
            };
            storeAppearance(next);
            return next;
        });
    };

    const chooseLayout = (nextLayout) => {
        const value = nextLayout === "combined" ? "combined" : "separate";
        setLayout(value);
        storeLayout(value);
    };

    const updateSeriesColor = (seriesId, color) => {
        if (!isHexColor(color)) {
            return;
        }
        setSelectedCharts((prev) => prev.map((item) => (
            item.id === seriesId ? { ...item, color, colorCustom: true } : item
        )));
    };

    const toggleSeriesVisible = useCallback((seriesId) => {
        setSelectedCharts((prev) => prev.map((item) => (
            item.id === seriesId ? { ...item, visible: item.visible === false } : item
        )));
    }, []);

    const updateSeriesAxis = (seriesId, yAxis) => {
        setSelectedCharts((prev) => prev.map((item) => (
            item.id === seriesId ? { ...item, yAxis: yAxis === "y1" ? "y1" : "y" } : item
        )));
    };

    useEffect(() => {
        setAppearance((prev) => {
            const selectorStyles = selectedCharts.map((chart) => (
                chart.colorCustom && isHexColor(chart.color)
                    ? { color: chart.color, colorCustom: true }
                    : { color: null, colorCustom: false }
            ));
            if (JSON.stringify(prev.selectorStyles || []) === JSON.stringify(selectorStyles)) {
                return prev;
            }
            const next = { ...prev, selectorStyles };
            storeAppearance(next);
            return next;
        });
    }, [selectedCharts]);

    const scheduleLive = (delayMs) => {
        clearRuntimeTimer(liveRuntime.current);
        liveRuntime.current.timer = setTimeout(() => {
            liveRuntime.current.timer = null;
            if (liveRequestsAllowed(liveRuntime.current, liveConfig.current) && !liveRuntime.current.running) {
                void runLiveFetchRef.current();
            }
        }, delayMs);
    };

    const runLiveFetch = async () => {
        if (liveRuntime.current.running || !liveRequestsAllowed(liveRuntime.current, liveConfig.current)) {
            return;
        }
        const charts = (chartsRef.current || []).filter((item) => item.tagId && !chartIsString(item));
        if (charts.length === 0) {
            const selected = (chartsRef.current || []).filter((item) => item.tagId);
            setLiveStatus("error");
            setLiveMessage(selected.some(chartIsString)
                ? STRING_CHART_NOTE
                : "Select at least one tag before live monitoring can request history.");
            return;
        }

        const full = liveRuntime.current.fullNext !== false;
        liveRuntime.current.fullNext = false;
        const bounds = liveQueryBounds(Date.now(), liveConfig.current.windowMinutes, {
            full,
            lastSuccessMs: liveRuntime.current.lastSuccessMs,
        });
        const startParam = new Date(bounds.queryStartMs).toISOString();
        const endParam = new Date(bounds.queryEndMs).toISOString();
        liveRuntime.current.running = true;
        const generation = liveRuntime.current.generation + 1;
        liveRuntime.current.generation = generation;
        const controller = new AbortController();
        liveRuntime.current.controller = controller;
        setLiveStatus("loading");
        setLiveMessage("");

        try {
            const tagIds = [...new Set(charts.map((item) => Number(item.tagId)))];
            const previousHistoryCursor = liveRuntime.current.historyCursor;
            const previousEventCursor = liveRuntime.current.statusEventCursor;
            const useHistoryCursor = !full && Number.isInteger(previousHistoryCursor);
            const useEventCursor = liveConfig.current.statusEventsEnabled
                && !full && Number.isInteger(previousEventCursor);
            const response = await API.get("/tag-history-batch/", {
                params: {
                    tag_ids: tagIds.join(","),
                    start_time: startParam,
                    end_time: endParam,
                    include_status_events: liveConfig.current.statusEventsEnabled ? "true" : undefined,
                    after_id: useHistoryCursor ? previousHistoryCursor : undefined,
                    after_event_id: useEventCursor ? previousEventCursor : undefined,
                },
                signal: controller.signal,
            });
            const history = response?.data?.history;
            if (!history || typeof history !== "object" || Array.isArray(history)) {
                throw new Error("The live history response is invalid.");
            }
            const statusEvents = response?.data?.status_events || {};
            const historyIncremental = response?.data?.history_mode === "incremental";
            const eventsIncremental = response?.data?.status_events_mode === "incremental";
            const results = charts.map((chartItem) => ({
                id: chartItem.id,
                ok: true,
                rows: Array.isArray(history[String(chartItem.tagId)])
                    ? history[String(chartItem.tagId)]
                    : [],
                events: liveConfig.current.statusEventsEnabled
                    && Array.isArray(statusEvents[String(chartItem.tagId)])
                    ? statusEvents[String(chartItem.tagId)]
                    : [],
            }));

            if (!responseIsCurrent(liveRuntime.current, generation, controller.signal.aborted)) {
                return;
            }
            liveRuntime.current.historyCursor = Number.isInteger(response?.data?.history_cursor)
                ? response.data.history_cursor
                : null;
            if (liveConfig.current.statusEventsEnabled) {
                liveRuntime.current.statusEventCursor = Number.isInteger(response?.data?.status_event_cursor)
                    ? response.data.status_event_cursor
                    : null;
            }
            const failures = results.filter((item) => !item.ok);
            const updates = new Map(results.filter((item) => item.ok).map((item) => [item.id, item]));
            if (updates.size > 0) {
                const reconciled = new Map();
                setLiveData((prev) => {
                    const next = { ...prev };
                    updates.forEach((update, id) => {
                        const previous = prev[id] || emptyHistory();
                        const chart = (chartsRef.current || []).find((item) => item.id === id) || { id };
                        const rows = historyIncremental
                            ? reconcileIncrementalHistory(
                                previous.tableData,
                                update.rows,
                                bounds.windowStartMs,
                                bounds.windowEndMs,
                                previousHistoryCursor
                            )
                            : reconcileLiveHistory(
                                previous.tableData,
                                update.rows,
                                bounds.windowStartMs,
                                bounds.windowEndMs
                            );
                        const events = eventsIncremental
                            ? reconcileIncrementalHistory(
                                previous.statusEvents,
                                update.events,
                                bounds.windowStartMs,
                                bounds.windowEndMs,
                                previousEventCursor
                            )
                            : reconcileStatusEvents(
                                previous.statusEvents,
                                update.events,
                                bounds.windowStartMs,
                                bounds.windowEndMs
                            );
                        reconciled.set(id, rows);
                        next[id] = historyPayload(withHistoryRows(chart, rows, events, {
                            preSorted: true,
                            live: true,
                        }));
                    });
                    return next;
                });
                setSelection((current) => {
                    if (liveConfig.current.mode !== "live" || !current || !reconciled.has(current.seriesId)) {
                        return current;
                    }
                    return remapLiveSelection(current, reconciled.get(current.seriesId));
                });
            }

            if (!liveRequestsAllowed(liveRuntime.current, liveConfig.current)) {
                return;
            }
            if (failures.length > 0) {
                setLiveStatus("error");
                setLiveMessage("A live refresh failed for one or more tags. The last successful samples for those tags are still shown.");
            } else {
                liveRuntime.current.lastSuccessMs = Date.now();
                setLiveUpdatedAt(new Date().toISOString());
                setLiveStatus("live");
                setLiveMessage("");
            }
        } catch (error) {
            liveRuntime.current.fullNext = true;
            if (!isAbortError(error) && !controller.signal.aborted
                && responseIsCurrent(liveRuntime.current, generation, false)) {
                console.error(
                    "Error fetching grouped live tag history:",
                    error.response ? error.response.data : error.message
                );
                setLiveStatus("error");
                setLiveMessage("The grouped live refresh failed. The last successful samples are still shown.");
            }
        } finally {
            if (liveRuntime.current.controller === controller) {
                liveRuntime.current.controller = null;
            }
            liveRuntime.current.running = false;
            if (liveRuntime.current.unmounted) {
                return;
            }
            if (generation !== liveRuntime.current.generation) {
                if (liveRequestsAllowed(liveRuntime.current, liveConfig.current)) {
                    void runLiveFetchRef.current();
                }
                return;
            }
            if (liveRequestsAllowed(liveRuntime.current, liveConfig.current)) {
                scheduleLive(liveConfig.current.refreshSeconds * 1000);
            }
        }
    };
    runLiveFetchRef.current = runLiveFetch;
    const liveTagKey = selectedCharts.map((item) => `${item.id}:${item.tagId}`).join("|");

    useEffect(() => {
        const runtime = liveRuntime.current;
        const range = rangeRuntime.current;
        runtime.unmounted = false;
        range.unmounted = false;
        return () => {
            runtime.unmounted = true;
            range.unmounted = true;
            stopLiveRuntime(runtime);
            range.generation += 1;
            range.controller?.abort();
            range.controller = null;
        };
    }, []);

    useEffect(() => {
        const onVisibility = () => {
            const hidden = document.hidden;
            setLiveHidden(hidden);
            if (hidden) {
                clearRuntimeTimer(liveRuntime.current);
                liveRuntime.current.generation += 1;
                liveRuntime.current.controller?.abort();
                liveRuntime.current.controller = null;
                return;
            }
            liveRuntime.current.fullNext = true;
            if (liveRequestsAllowed(liveRuntime.current, liveConfig.current) && !liveRuntime.current.running) {
                void runLiveFetchRef.current();
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    useEffect(() => {
        stopLiveRuntime(liveRuntime.current);
        if (!liveRequestsAllowed(liveRuntime.current, liveConfig.current)) {
            return undefined;
        }
        liveRuntime.current.fullNext = true;
        if (!liveRuntime.current.running) {
            void runLiveFetchRef.current();
        }
        return undefined;
    }, [dataMode, liveEnabled, livePaused, liveRefresh, liveTagKey, liveWindow]);

    useEffect(() => {
        storeLivePreferences({ windowMinutes: liveWindow, refreshSeconds: liveRefresh });
    }, [liveRefresh, liveWindow]);

    const enableLive = () => {
        liveConfig.current.enabled = true;
        liveConfig.current.paused = false;
        setLiveEnabled(true);
        setLivePaused(false);
        setFollowLatest(true);
        setLiveStatus("loading");
        setLiveMessage("");
    };

    const disableLive = () => {
        liveConfig.current.enabled = false;
        liveConfig.current.paused = false;
        setLiveEnabled(false);
        setLivePaused(false);
        setLiveStatus("off");
        setLiveMessage("");
        setLiveHidden(false);
    };

    const pauseLive = () => {
        liveConfig.current.paused = true;
        setLivePaused(true);
        setLiveStatus("paused");
    };

    const resumeLive = () => {
        liveConfig.current.paused = false;
        setLivePaused(false);
        setLiveStatus("loading");
        setLiveMessage("");
    };

    const pauseFollowForManualZoom = useCallback(() => {
        if (liveConfig.current.enabled) {
            setFollowLatest(false);
        }
    }, []);

    const resumeLiveFollow = useCallback(() => {
        setFollowLatest(true);
        setZoomFlags((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    }, []);

    const resetChartView = useCallback((chart) => {
        if (liveConfig.current.enabled) {
            setFollowLatest(true);
            setFollowEpoch((value) => value + 1);
            setZoomFlags({});
            return;
        }
        chart?.resetZoom?.("none");
    }, []);

    const fetchAllTagData = async () => {
        const validCharts = selectedCharts.filter((item) => item.tagId && !chartIsString(item));
        if (validCharts.length === 0) {
            const selected = selectedCharts.filter((item) => item.tagId);
            if (selected.some(chartIsString)) {
                setHistoryError(STRING_CHART_NOTE);
            } else {
                alert("Please select at least one tag.");
            }
            return;
        }
        const startParam = historyQueryTime(startTime, "start");
        const endParam = historyQueryTime(endTime, "end");
        if (!startParam || !endParam || Date.parse(startParam) >= Date.parse(endParam)) {
            setHistoryError("Choose a start time that is earlier than the end time.");
            alert("Choose a start time that is earlier than the end time.");
            return;
        }

        rangeRuntime.current.generation += 1;
        const generation = rangeRuntime.current.generation;
        rangeRuntime.current.controller?.abort();
        const controller = new AbortController();
        rangeRuntime.current.controller = controller;
        setHistoryLoading(true);
        setHistoryError("");

        try {
            const tagIds = [...new Set(validCharts.map((item) => Number(item.tagId)))];
            const response = await API.get("/tag-history-batch/", {
                params: {
                    tag_ids: tagIds.join(","),
                    start_time: startParam,
                    end_time: endParam,
                },
                signal: controller.signal,
            });
            const history = response?.data?.history || {};
            const updatedCharts = await Promise.all(validCharts.map(async (chartItem) => {
                const data = Array.isArray(history[String(chartItem.tagId)])
                    ? history[String(chartItem.tagId)]
                    : [];
                const events = statusEventsEnabled
                    ? await loadStatusEvents(chartItem.tagId, startParam, endParam)
                    : [];
                return withHistoryRows(chartItem, data, events);
            }));
            if (!responseIsCurrent(rangeRuntime.current, generation, controller.signal.aborted)) {
                return;
            }
            setRangeData((prev) => {
                const next = { ...prev };
                updatedCharts.forEach((item) => {
                    next[item.id] = historyPayload(item);
                });
                return next;
            });
            setSelection(null);
            setZoomFlags({});
        } catch (error) {
            if (isAbortError(error) || controller.signal.aborted) {
                return;
            }
            if (!responseIsCurrent(rangeRuntime.current, generation, false)) {
                return;
            }
            console.error(
                "Error fetching tag history:",
                error.response ? error.response.data : error.message
            );
            setHistoryError("Error fetching one or more charts.");
            alert("Error fetching one or more charts.");
        } finally {
            if (rangeRuntime.current.controller === controller) {
                rangeRuntime.current.controller = null;
            }
            if (responseIsCurrent(rangeRuntime.current, generation, false)) {
                setHistoryLoading(false);
            }
        }
    };

    const downloadExcel = async () => {
        const request = historyExportRequest({
            charts: selectedCharts,
            mode: dataMode,
            startTime,
            endTime,
            nowMs: Date.now(),
            liveWindowMinutes: liveWindow,
            timeZone: browserTimeZone(),
        });
        if (!request.ok) {
            setExportMessage(request.error);
            alert(request.error);
            return;
        }
        setExporting(true);
        setExportMessage("");
        try {
            const response = await API.post("/history-export/", request.body, { responseType: "blob" });
            const outcome = acceptExportResponse(response.headers || {});
            if (!outcome.ok) {
                setExportMessage(outcome.error);
                alert(outcome.error);
                return;
            }
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = outcome.split ? "aqueris-history-complete-split.xlsx" : "aqueris-history-complete.xlsx";
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            const splitNote = outcome.split
                ? ` Excel's row limit split the result across ${outcome.sheets} sheets. Every persisted record is included.`
                : "";
            setExportMessage(
                outcome.rows === 0
                    ? "The export is complete. No persisted records were in the selected interval."
                    : `Exported ${outcome.rows} persisted records. Timestamps in the file are UTC.${splitNote}`
            );
        } catch (error) {
            let serverDetail = "";
            const data = error?.response?.data;
            if (data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await data.text());
                    serverDetail = parsed.detail || parsed.error || "";
                } catch (parseError) {
                    serverDetail = "";
                }
            } else if (typeof error?.response?.data?.detail === "string") {
                serverDetail = error.response.data.detail;
            }
            const detail = describeExportFailure(error?.response?.status, serverDetail);
            setExportMessage(detail);
            alert(detail);
        } finally {
            setExporting(false);
        }
    };

    const applyLoadedPreset = (restored) => {
        const charts = restored.charts.map((chart) => ({
            ...chart,
            chartNote: chartIsString(chart) ? STRING_CHART_NOTE : "",
        }));
        const next = presetApplication(appearanceRef.current, { ...restored, charts });
        setSelectedCharts(next.charts);
        setAppearance(next.appearance);
        storeAppearance(next.appearance);
        setLayout(next.layout);
        storeLayout(next.layout);
        setRangeData({});
        setLiveData({});
        setSelection(null);
        setZoomFlags({});
    };

    const tagRows = Array.isArray(tags) ? tags : [];
    const numericTagRows = tagRows.filter(
        (tag) => tag.signal_type !== "STRING" && tag.opc_data_type !== "String"
    );
    const viewCharts = useMemo(() => {
        const store = dataMode === "live" ? liveData : rangeData;
        return selectedCharts.map((chart) => {
            const stored = store[chart.id];
            if (!stored) {
                return {
                    ...chart,
                    ...emptyHistory(),
                    chartNote: chartIsString(chart) ? STRING_CHART_NOTE : "",
                };
            }
            return { ...chart, ...stored };
        });
    }, [dataMode, liveData, rangeData, selectedCharts]);
    const seriesViews = useMemo(() => viewCharts.map((chart, index) => {
        const name = chart.tagName || `Tag ${chart.tagId || index + 1}`;
        const sameName = viewCharts.filter((item) => item.tagName && item.tagName === chart.tagName);
        const position = sameName.findIndex((item) => item.id === chart.id) + 1;
        return {
            chart,
            index,
            color: displayColor(chart, index, theme),
            label: sameName.length > 1 ? `${name} (${position})` : name,
        };
    }), [theme, viewCharts]);
    const loadedSeries = useMemo(
        () => seriesViews.filter((item) => item.chart.chartData),
        [seriesViews]
    );
    const gridHex = appearance.gridColor || defaultGridHex(theme);
    const gridOpacity = appearance.gridOpacity == null ? defaultGridOpacity(theme) : appearance.gridOpacity;
    const liveWarning = liveLoadWarning({
        windowMinutes: liveWindow,
        refreshSeconds: liveRefresh,
        tagCount: selectedCharts.filter((item) => item.tagId).length,
    });
    const liveStatusLabel = {
        off: "Off",
        live: "Live",
        paused: "Paused",
        loading: "Loading",
        error: "Error",
    }[liveStatus] || "Off";

    return (
        <div className="graph-page" data-theme={theme}>
            <header className="graph-page__top">
                <div>
                    <p className="graph-page__eyebrow">History and analytics</p>
                    <h1>Tag Data Graph</h1>
                    <p className="graph-page__subtitle">
                        Choose tags, then open either the time range or live monitoring. Excel exports every persisted record in that interval.
                    </p>
                </div>
            </header>

            <div className="graph-page__tabs" role="tablist" aria-label="History mode">
                <button
                    type="button"
                    className="graph-page__tab"
                    role="tab"
                    id="history-tab-range"
                    aria-selected={dataMode === "range"}
                    aria-controls="history-panel-range"
                    onClick={() => chooseDataMode("range")}
                >
                    Time Range
                </button>
                <button
                    type="button"
                    className="graph-page__tab"
                    role="tab"
                    id="history-tab-live"
                    aria-selected={dataMode === "live"}
                    aria-controls="history-panel-live"
                    onClick={() => chooseDataMode("live")}
                >
                    Live Monitoring
                </button>
            </div>

            <TimeRangePanel
                ranges={QUICK_RANGES}
                activeRange={activeRange}
                startTime={startTime}
                endTime={endTime}
                loading={historyLoading}
                error={historyError}
                hidden={dataMode !== "range"}
                onQuickRange={setQuickTimeRange}
                onStartChange={(value) => {
                    setActiveRange("");
                    setStartTime(value);
                }}
                onEndChange={(value) => {
                    setActiveRange("");
                    setEndTime(value);
                }}
                onFetch={fetchAllTagData}
            />

            <LiveMonitoringPanel
                hidden={dataMode !== "live"}
                status={liveStatus}
                statusLabel={liveStatusLabel}
                windowMinutes={liveWindow}
                refreshSeconds={liveRefresh}
                windows={LIVE_WINDOWS}
                intervals={LIVE_INTERVALS}
                enabled={liveEnabled}
                paused={livePaused}
                followLatest={followLatest}
                pageHidden={liveHidden}
                updatedAt={liveUpdatedAt}
                warning={liveWarning}
                message={liveMessage}
                loading={historyLoading}
                windowLabel={liveWindowLabel}
                onWindowChange={setLiveWindow}
                onRefreshChange={setLiveRefresh}
                onToggleEnabled={liveEnabled ? disableLive : enableLive}
                onTogglePaused={livePaused ? resumeLive : pauseLive}
                onFollowLatest={() => {
                    setFollowLatest(true);
                    setFollowEpoch((value) => value + 1);
                }}
            />

            <div className="graph-page__tabs" role="tablist" aria-label="Graph control panels">
                {[
                    ["presets", "Favorite Graph Presets"],
                    ["display", "Chart Display"],
                    ["status", "Connection Status"],
                ].map(([id, label]) => (
                    <button
                        key={id}
                        type="button"
                        className="graph-page__tab"
                        role="tab"
                        aria-selected={activeControlTab === id}
                        onClick={() => setActiveControlTab((current) => current === id ? "" : id)}
                    >
                        {label}
                    </button>
                ))}
            </div>
            <StatusEventsControl
                open={activeControlTab === "status"}
                enabled={statusEventsEnabled}
                onChange={(enabled) => {
                    setStatusEventsEnabled(enabled);
                    storeStatusEventsEnabled(enabled);
                    if (!enabled) {
                        setRangeData((current) => Object.fromEntries(Object.entries(current).map(([key, value]) => [key, { ...value, statusEvents: [] }])));
                        setLiveData((current) => Object.fromEntries(Object.entries(current).map(([key, value]) => [key, { ...value, statusEvents: [] }])));
                    }
                }}
            />
            <ChartDisplayPanel
                open={activeControlTab === "display"}
                layout={layout}
                appearance={appearance}
                gridColor={gridHex}
                gridOpacity={gridOpacity}
                onLayoutChange={chooseLayout}
                onAppearanceChange={updateAppearance}
                onReset={resetAppearance}
            />
            <GraphPresetBar
                open={activeControlTab === "presets"}
                charts={selectedCharts}
                appearance={appearance}
                layout={layout}
                tags={tagRows}
                tagsReady={!tagsLoading && !tagsError}
                tagsError={tagsError}
                liveEnabled={liveEnabled}
                dataMode={dataMode}
                onApply={applyLoadedPreset}
            />


            <TagSelectionPanel
                series={seriesViews}
                tags={numericTagRows}
                tagsLoading={tagsLoading}
                tagsError={tagsError}
                layout={layout}
                selectedCount={selectedCharts.length}
                stringNote={STRING_CHART_NOTE}
                exporting={exporting}
                exportMessage={exportMessage}
                isStringChart={chartIsString}
                onTagChange={updateChartTag}
                onColorChange={updateSeriesColor}
                onAxisChange={updateSeriesAxis}
                onToggleVisible={toggleSeriesVisible}
                onRemove={removeChartSelector}
                onAdd={addNewChartSelector}
                onExport={downloadExcel}
            />

            <section className="graph-page__results" aria-live="polite">
                {dataMode === "range" && !historyLoading && loadedSeries.length === 0 && (
                    <div className="graph-page__card">
                        <p className="graph-page__empty">
                            No time-range chart is loaded yet. Select at least one tag, then click Fetch Data.
                        </p>
                    </div>
                )}

                {dataMode === "live" && !historyLoading && loadedSeries.length === 0 && liveStatus !== "loading" && (
                    <div className="graph-page__card">
                        <p className="graph-page__empty">
                            Live monitoring is separate from the time-range chart. Enable Live to load the selected window.
                        </p>
                    </div>
                )}

                {dataMode === "live" && liveStatus === "loading" && loadedSeries.length === 0 && (
                    <div className="graph-page__card">
                        <p className="graph-page__empty">Loading the live window…</p>
                    </div>
                )}

                {layout === "combined" && loadedSeries.length > 0 && (
                    <CombinedTrend
                        series={loadedSeries}
                        theme={theme}
                        appearance={appearance}
                        selection={selection}
                        zoomed={liveEnabled ? !followLatest || Boolean(zoomFlags[COMBINED_CHART_ID]) : Boolean(zoomFlags[COMBINED_CHART_ID])}
                        preserveViewport={liveEnabled && !followLatest}
                        followLatest={followLatest}
                        followEpoch={followEpoch}
                        onSelect={selectRecord}
                        onZoomChange={syncZoomFlag}
                        onToggleVisible={toggleSeriesVisible}
                        onManualZoom={pauseFollowForManualZoom}
                        onResumeFollow={resumeLiveFollow}
                        onResetZoom={resetChartView}
                        cursorActive={Boolean(selection)}
                        onClearCursor={clearCursor}
                    />
                )}

                {layout === "separate" && loadedSeries.map((item) => (
                    <TagTrend
                        key={item.chart.id}
                        chart={item.chart}
                        color={item.color}
                        label={item.label}
                        theme={theme}
                        appearance={appearance}
                        selectedIndex={selection?.seriesId === item.chart.id ? selection.index : null}
                        zoomed={liveEnabled ? !followLatest || Boolean(zoomFlags[item.chart.id]) : Boolean(zoomFlags[item.chart.id])}
                        axisMode={liveEnabled ? "time" : "category"}
                        preserveViewport={liveEnabled && !followLatest}
                        followLatest={followLatest}
                        followEpoch={followEpoch}
                        onSelect={selectRecord}
                        onZoomChange={syncZoomFlag}
                        onManualZoom={pauseFollowForManualZoom}
                        onResumeFollow={resumeLiveFollow}
                        onResetZoom={resetChartView}
                        cursorActive={Boolean(selection)}
                        onClearCursor={clearCursor}
                    />
                ))}

                <RecordsPanel
                    open={recordsOpen}
                    series={loadedSeries}
                    selection={selection}
                    onToggle={toggleRecords}
                    onSelect={selectRecord}
                />
            </section>
        </div>
    );
};

export default GraphPage;
